'use client';

import React, { FC, useCallback, useEffect } from 'react';
import { useRouter } from 'next/navigation';

import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { standardSchemaResolver } from '@hookform/resolvers/standard-schema';

import { LoaderCircle } from 'lucide-react';

import { Input } from 'components/ui/input';
import { Button } from 'components/ui/button';

import { useSaveSocialMutation } from 'generated/hooks';
import { useFormErrorHandling } from 'hooks/useFormErrorHandling';
import { MEMBER_ROUTES } from 'utils/routes';
import { useMedia } from 'react-use';
import { useTelegramMiniApp } from 'app/_context/TelegramContext';

const schema = z.object({
  profileLink: z.string()
    .trim()
    .url({ message: 'Please provide a valid URL' })
});

type LinkedinForm = {
  profileLink: string;
};

interface IWelcomeForm {
  onSuccess?: () => void;
}

export const WelcomeForm: FC<IWelcomeForm> = ({ onSuccess }) => {
  const {
    control,
    formState: { errors },
    handleSubmit,
    setError,
  } = useForm<LinkedinForm>({
    mode: 'onChange',
    reValidateMode: 'onSubmit',
    resolver: standardSchemaResolver(schema),
    defaultValues: {
      profileLink: '',
    },
  });

  const router = useRouter();
  const { handleErrors } = useFormErrorHandling();
  const [state, saveSocialMutation] = useSaveSocialMutation();
  const { isEnabledTWA, setMainButtonTWA, hideMainButtonTWA, offClickMainButtonTWA } = useTelegramMiniApp();

  const isMobile = useMedia('(max-width: 768px)', false);

  const onSubmit = useCallback(async (data: LinkedinForm) => {
    try {
      const response = await saveSocialMutation({
        linkLinkedin: data.profileLink,
      });

      if (response.error) {
        return handleErrors<LinkedinForm>(response.error, setError, {});
      }

      onSuccess?.();
      router.push(MEMBER_ROUTES.WELCOME_LOADING.getPath());
    } catch (error) {
      console.error(error);
    }
  }, [handleErrors, onSuccess, router, saveSocialMutation, setError]);

  useEffect(() => {
    const submitHandler = handleSubmit(onSubmit);
    const handler = () => {
      submitHandler();
      hideMainButtonTWA(submitHandler);
    }

    setMainButtonTWA('Connect LinkedIn', handler);

    return () => {
      offClickMainButtonTWA(handler);
      hideMainButtonTWA(handler);
    };
  }, [handleSubmit, isEnabledTWA, onSubmit, setMainButtonTWA, hideMainButtonTWA, offClickMainButtonTWA]);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-10 md:gap-6">
      <Controller
        control={control}
        name="profileLink"
        render={({ field, formState: { errors } }) => (
          <Input
            label="Your LinkedIn"
            placeholder="https://www.linkedin.com/in/your_profile"
            errorText={errors.profileLink?.message as string || errors.root?.message}
            className="bg-transparent"
            {...field}
          />
        )}
      />
      {!isEnabledTWA && (
        <Button disabled={state.fetching} variant='primaryBrand' size={isMobile ? 'large' : 'x-large'}
                className='w-full'>
          {state.fetching && <LoaderCircle className='animate-spin' />}
          Save & Finish
        </Button>
      )}
    </form>
  )
};
